import { useEffect, useMemo, useState } from "react"
import _ from "underscore";
import services from "../../../services/services";
import { decryptData, encryptData } from "../../../utils/encrypt";

export const useDriveSpace = ({ projectChanged }) => {

    const [driveData, setDriveData] = useState({ x_axis: [], y_axis: { total: [], free: [], used: [], actual_total: [] }, api_data: [] });
    const [showPopup, setShowPopup] = useState(false);
    const [popupData, setPopupData] = useState([]);
    const [loader, setLoader] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [filterOpts, setFilterOpts] = useState({ drive: [], server: [] });
    const [filterValues, setFilterValues] = useState({ drive: [], server: [] });
    const [filterCount, setFilterCount] = useState(0);
    const [expand, setExpand] = useState(false);
    const [intervalFlag, setIntervalFlag] = useState(false);

    useEffect(() => {
        getData();
    }, [projectChanged])

    useEffect(() => {
        const interval = setInterval(() => {
            getData();
        }, 120000);

        return () => clearInterval(interval);
    }, [intervalFlag]);

    const getData = async () => {
        try {
            const selectedProject = sessionStorage.getItem("inspectra_project") ? JSON.parse(decryptData(sessionStorage.getItem("inspectra_project"))) : {};
            if (!_.isEmpty(selectedProject)) {
                setLoader(true);
                const filters = JSON.parse(decryptData(sessionStorage.getItem("drivefilters")) || "{}");
                const payload = {
                    type: 'drivespace',
                    drive_name: _.isEmpty(filters?.drive) ? undefined : filters?.drive?.toString(),
                    server: _.isEmpty(filters?.server) ? undefined : filters?.server?.toString(),
                    project_name: selectedProject?.project_key
                }
                const response = await services.callInspectra("GET", '/elsinspectra/top', payload);
                if (response) {
                    const data = response?.data;
                    const secondData = await getSecondData(payload, selectedProject);

                    const drives = _.uniq(_.pluck(data, "drive_name"))?.concat(_.uniq(_.pluck(secondData, "DriveName")));
                    const servers = _.uniq(_.pluck(data, "server"))?.concat(_.uniq(_.pluck(secondData, "server")));
                    if (_.isEmpty(filters?.drive) && _.isEmpty(filters?.server)) {
                        setFilterOpts((prev) => ({ ...prev, drive: drives, server: servers }))
                    }
                    setDriveData((prev) => ({ ...prev, api_data: data?.concat(secondData) }));
                    let sizeObj = { total: [], free: [], used: [], actual_total: [] };
                    let xdrives = [];
                    drives?.forEach((drive) => {
                        servers?.forEach((server) => {
                            let findData = _.findWhere(data, { drive_name: drive, server });
                            if (_.isEmpty(findData)) {
                                findData = _.findWhere(secondData, { DriveName: drive, server });
                            }
                            if (!_.isEmpty(findData)) {
                                xdrives.push(`${server} (${drive})`);
                                if (!findData?.size?.includes("G") && !findData?.size?.includes("M") && !findData?.size?.includes("T")) {
                                    const total = (parseFloat(findData?.size / 1024) - (parseFloat(findData?.available / 1024) + parseFloat(findData?.used / 1024)))
                                    sizeObj.total.push(parseInt(total));
                                    sizeObj.free.push(parseInt(findData?.available / 1024));
                                    sizeObj.used.push(parseInt(findData?.used / 1024));
                                    sizeObj.actual_total.push(parseInt(findData?.size / 1024));
                                } else {
                                    const used = parseInt(findData?.used?.includes("T") ? (findData?.used?.split("T")?.[0] * 1024) : findData?.used?.includes("G") ? findData?.used?.split("G")?.[0] : (findData?.used?.split("M")?.[0] / 1024));

                                    const available = parseInt(findData?.available?.includes("T") ? (findData?.available?.split("T")?.[0] * 1024) : findData?.available?.includes("G") ? findData?.available?.split("G")?.[0] : (findData?.available?.split("M")?.[0] / 1024));

                                    const size = parseInt(findData?.size?.includes("T") ? (findData?.size?.split("T")?.[0] * 1024) : findData?.size?.includes("G") ? findData?.size?.split("G")?.[0] : (findData?.size?.split("M")?.[0] / 1024));

                                    const total = size - (available + used);
                                    sizeObj.total.push(parseInt(total));
                                    sizeObj.free.push(available);
                                    sizeObj.used.push(used);
                                    sizeObj.actual_total.push(size);
                                }
                            }
                        })
                    })
                    setDriveData((prev) => ({ ...prev, x_axis: xdrives, y_axis: { total: sizeObj?.total, free: sizeObj?.free, used: sizeObj?.used, actual_total: sizeObj?.actual_total } }));
                    setLoader(false);
                }
            }
        } catch (error) {
            console.error("Error: ", error);
            setLoader(false);
        }
    }

    const getSecondData = async (pastPayload, selectedProject) => {
        try {
            const payload = {
                drivename: pastPayload?.drive_name,
                server: pastPayload?.server,
                project_name: selectedProject?.project_key
            }
            const hostArray = _.pluck(selectedProject?.host, "domain_host_key");
            const responses = await Promise.all(
                hostArray.map(async (host) => {
                    try {
                        const response = await services.callInspectra("POST", '/inspectra/driveDetails/v1', payload, host);
                        return response?.data?.[0];
                    } catch (err) {
                        console.error(`Error fetching data for host ${host}`, err);
                        return null;
                    }
                })
            );

            const dataArray = responses.filter(Boolean).flat();
            if (dataArray) {
                const data = dataArray;
                return data;
            }
        } catch (error) {
            console.error("Error: ", error);
            setLoader(false);
        }
    }

    const options = {
        credits: { enabled: false },
        chart: {
            type: 'column',
            events: {
                render: function () {
                    const chart = this;
                    chart.xAxis[0].ticks &&
                        Object.values(chart.xAxis[0].ticks).forEach(tick => {
                            if (tick.label && !tick.label.clickAttached) {
                                tick.label.element.style.cursor = 'pointer';
                                tick.label.element.onclick = () => {
                                    const label = tick.label.textStr.replace(/<[^>]+>/g, '');
                                    const match = label.match(/(\S+)\s\((.*?)\)/);
                                    if (match) {
                                        const server = match[1];
                                        const drive = match[2];
                                        handleShowDetailsPopup(server, drive);
                                    }
                                };
                                tick.label.clickAttached = true;
                            }
                        });
                }
            }
        },
        title: {
            text: ''
        },
        xAxis: {
            categories: driveData?.x_axis,
            labels: {
                useHTML: true,
                formatter: function () {
                    const label = this.value;
                    const match = label.match(/^(.*?)\s\((.*?)\)$/);

                    if (match) {
                        const server = match[1];
                        const drive = match[2];
                        const fullText = `${server} (${drive})`;

                        return `
                            <div title="${fullText}"><span style="font-weight: bold">${server}</span> (${drive})</div>
                        `;
                    }
                    return label;
                }
            },
            gridLineDashStyle: 'solid',
            gridLineWidth: 1
        },
        yAxis: {
            type: 'logarithmic',
            title: {
                text: ''
            },
            max: Math.max(...driveData?.y_axis?.actual_total?.map(item => parseInt(item))),
        },
        legend: {
            layout: 'horizontal',
            align: 'center',
            verticalAlign: 'bottom',
            floating: false,
            backgroundColor: 'white',
            shadow: false,
            itemStyle: {
                fontSize: '12px'
            }
        },
        tooltip: {
            shared: false,
            formatter: function () {
                const category = this.point.category;
                const seriesName = this.series.name;
                const match = category.match(/^(.*?)\s\((.*?)\)$/);
                if (!match) return;

                const server = match[1];
                const drive = match[2];

                let rawData = _.findWhere(driveData.api_data, { server: server?.trim(), drive_name: drive?.trim() });
                if (_.isEmpty(rawData)) {
                    rawData = _.findWhere(driveData.api_data, { server: server?.trim(), DriveName: drive?.trim() });
                }

                let actualTotal = rawData ? parseInt(rawData?.size?.includes("T") ? (rawData?.size?.split("T")?.[0] * 1024) : rawData?.size?.includes("G") ? rawData?.size?.split("G")?.[0] : (rawData?.size?.split("M")?.[0] / 1024)) : ((parseFloat(rawData?.size) / 1024)?.toFixed(2));
                return `<b>${category}</b><br/><span style="color:${this.color}">\u25CF</span> ${seriesName}: <b>${this.y} GB</b><br/><span style="color:transparent">\u25CF</span> Total: <b>${actualTotal || ""} GB</b>`;
            }
        }
        ,
        plotOptions: {
            series: {
                cursor: 'pointer',
                point: {
                    events: {
                        click: function () {
                            const label = this.category;
                            const match = label.match(/^(.*?)\s\((.*?)\)$/);
                            if (match) {
                                const server = match[1];
                                const drive = match[2];
                                handleShowDetailsPopup(server, drive);
                            }
                        }
                    }
                },
                enableMouseTracking: true,
            },
            column: {
                stacking: 'normal',
            }
        },
        series: [
            {
                name: "Free (GB)",
                data: driveData?.y_axis?.free,
                color: '#56b047',
            },
            {
                name: "Used (GB)",
                data: driveData?.y_axis?.used,
                color: '#5C8AF6',
            },
        ],
    }

    const handleShowDetailsPopup = (server, drive) => {
        let data = _.findWhere(driveData?.api_data, { server: server?.trim(), drive_name: drive?.trim() });
        if (_.isEmpty(data)) {
            data = _.findWhere(driveData?.api_data, { server: server?.trim(), DriveName: drive?.trim() });
        }
        setPopupData(data);
        setShowPopup(true);
    }

    const handleApplyFilters = () => {
        const driveFilters = {
            server: filterValues?.server || [],
            drive: filterValues?.drive || []
        }
        sessionStorage.setItem("drivefilters", encryptData(JSON.stringify(driveFilters)));

        // set counts of the filters applied
        let count = 0;
        Object.keys(filterValues)?.forEach((item) => {
            if (filterValues[item].length > 0) {
                count++;
            }
        })
        setFilterCount(count);
        getData();
        setIntervalFlag(!intervalFlag);
        setShowFilter(false);
    }

    return useMemo(() => ({
        options, showPopup, setShowPopup, popupData, loader, showFilter, setShowFilter, filterOpts, filterValues, setFilterValues, filterCount, expand, setExpand, handleApplyFilters
    }), [options, showPopup, setShowPopup, popupData, loader, showFilter, setShowFilter, filterOpts, filterValues, setFilterValues, filterCount, expand, setExpand, handleApplyFilters])
}